import { useContext } from 'react';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  formatTransactionListDate,
  formatTransactionListTime,
  getLocalizedTransactionText,
} from '../../../assets/services/transactions';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import TransactionCardIdentity from '../TransactionCardIdentity/TransactionCardIdentity';
import TransactionIcon from '../TransactionIcon/TransactionIcon';
import './TxCard.css';

function getTransactionMeta(transaction, language, history, timeOnly = false) {
  return timeOnly
    ? formatTransactionListTime(transaction, language)
    : formatTransactionListDate(transaction, language, history);
}

function getTransactionAmount(transaction) {
  const amount = transaction.amountFormatted ?? transaction.amount;

  return typeof amount === 'string'
    ? amount.replace(/^([+\-−])\s+/, '$1')
    : amount;
}

function isPositiveTransaction(transaction) {
  if (typeof transaction.amount === 'number') {
    return transaction.amount > 0;
  }

  return transaction.amountVariant === 'positive';
}

function isCanceledTransaction(transaction) {
  const operationType = String(
    transaction?.operationType
    || transaction?.raw?.type
    || transaction?.type
    || ''
  ).trim().toLowerCase();

  return transaction?.iconVariant === 'payment-canceled'
    || operationType === 'payment_canceled';
}

function TxCard({
  transactions,
  onSelect,
  className,
  showCardContext = false,
  showFullDate = false,
}) {
  const { history, language } = useContext(TranslationContext);
  const cardClassName = ['tx-card', className].filter(Boolean).join(' ');
  const handleSelect = (transaction) => {
    triggerImpactHaptic('light');
    onSelect?.(transaction);
  };

  return (
    <div className={cardClassName}>
      {transactions.map((transaction, index) => {
        const RowElement = onSelect ? 'button' : 'div';
        const isCanceled = isCanceledTransaction(transaction);
        const rowClassName = [
          'tx-card__row',
          index < transactions.length - 1 && 'tx-card__row--bordered',
          !onSelect && 'tx-card__row--static',
          isCanceled && 'tx-card__row--canceled',
        ]
          .filter(Boolean)
          .join(' ');
        const amountClassName = [
          'tx-card__amount',
          isPositiveTransaction(transaction) && !isCanceled && 'tx-card__amount--positive',
          isCanceled && 'tx-card__amount--canceled',
        ].filter(Boolean).join(' ');
        const transactionAmount = getTransactionAmount(transaction);
        const displayedAmount = isCanceled && typeof transactionAmount === 'string'
          ? transactionAmount.replace(/^[+\-−]/, '')
          : transactionAmount;
        const cardName = String(transaction.cardName || '').trim();

        return (
          <RowElement
            key={transaction.id}
            type={onSelect ? 'button' : undefined}
            className={rowClassName}
            onClick={onSelect ? () => handleSelect(transaction) : undefined}
          >
            <span className="tx-card__icon-stack">
              <TransactionIcon transaction={transaction} />
              {showCardContext && transaction.cardLast4 ? (
                <TransactionCardIdentity transaction={transaction} />
              ) : null}
            </span>
            <div className="tx-card__details">
              <span className="tx-card__main-row">
                <span className="tx-card__merchant">
                  {getLocalizedTransactionText(transaction.merchant, history)}
                </span>
                <span
                  className={amountClassName}
                  aria-label={isCanceled
                    ? `${history.canceledStatus || history.paymentCanceled}. ${displayedAmount}`
                    : undefined}
                >
                  {displayedAmount}
                </span>
              </span>
              <span className="tx-card__meta-row">
                {showCardContext && cardName ? (
                  <span className="tx-card__context">
                    <span className="tx-card__card-name">{cardName}</span>
                  </span>
                ) : null}
                <span
                  className={showCardContext ? 'tx-card__meta tx-card__meta--time' : 'tx-card__meta'}
                >
                  {getTransactionMeta(
                    transaction,
                    language,
                    history,
                    showCardContext && !showFullDate
                  )}
                </span>
              </span>
            </div>
          </RowElement>
        );
      })}
    </div>
  );
}

export default TxCard;
